import { useLanguage } from '../context/LanguageContext';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const SEO_META = {
  en: {
    title: 'Kaesthetic World | International B2B Travel Operator',
    description: 'We design and execute high-demand travel experiences connecting travel agencies between Europe and Asia. Beauty, Culture, Business & Lifestyle programs.',
    locale: 'en_GB',
  },
  es: {
    title: 'Kaesthetic World | Operador B2B de Viajes Internacional',
    description: 'Diseñamos y ejecutamos experiencias de viaje de alta demanda conectando agencias entre Europa y Asia. Programas de Belleza, Cultura, Negocios y Lifestyle.',
    locale: 'es_ES',
  },
  ko: {
    title: 'Kaesthetic World | 국제 B2B 여행 운영사',
    description: '유럽과 아시아 여행사를 연결하는 고수요 여행 경험을 설계하고 실행합니다. 뷰티, 문화, 비즈니스 & 라이프스타일 프로그램.',
    locale: 'ko_KR',
  },
  ja: {
    title: 'Kaesthetic World | 国際B2B旅行オペレーター',
    description: 'ヨーロッパとアジアの旅行代理店を結ぶ、需要の高い旅行体験を設計・実行。ビューティー、文化、ビジネス＆ライフスタイルプログラム。',
    locale: 'ja_JP',
  },
  zh: {
    title: 'Kaesthetic World | 国际B2B旅游运营商',
    description: '我们设计和执行连接欧洲和亚洲旅行社的高需求旅行体验。美容、文化、商务和生活方式项目。',
    locale: 'zh_CN',
  },
  fr: {
    title: 'Kaesthetic World | Opérateur B2B de Voyage International',
    description: 'Nous concevons et exécutons des expériences de voyage à forte demande reliant les agences entre l\'Europe et l\'Asie.',
    locale: 'fr_FR',
  },
  pt: {
    title: 'Kaesthetic World | Operador B2B de Viagens Internacional',
    description: 'Projetamos e executamos experiências de viagem de alta demanda conectando agências entre Europa e Ásia.',
    locale: 'pt_PT',
  },
  de: {
    title: 'Kaesthetic World | Internationaler B2B-Reiseoperator',
    description: 'Wir gestalten und führen stark nachgefragte Reiseerlebnisse durch, die Agenturen zwischen Europa und Asien verbinden.',
    locale: 'de_DE',
  },
  it: {
    title: 'Kaesthetic World | Operatore B2B di Viaggio Internazionale',
    description: 'Progettiamo ed eseguiamo esperienze di viaggio ad alta domanda collegando agenzie tra Europa e Asia.',
    locale: 'it_IT',
  },
  el: {
    title: 'Kaesthetic World | Διεθνής B2B Χειριστής Ταξιδιών',
    description: 'Σχεδιάζουμε και εκτελούμε ταξιδιωτικές εμπειρίες υψηλής ζήτησης συνδέοντας πρακτορεία μεταξύ Ευρώπης και Ασίας.',
    locale: 'el_GR',
  },
  ru: {
    title: 'Kaesthetic World | Международный B2B Туроператор',
    description: 'Мы разрабатываем и реализуем востребованные туристические программы, соединяя агентства между Европой и Азией.',
    locale: 'ru_RU',
  },
};

const LANG_CODES = ['en', 'es', 'ko', 'ja', 'zh', 'fr', 'pt', 'de', 'it', 'el', 'ru'];

const SEOHead = () => {
  const { language } = useLanguage();
  const meta = SEO_META[language] || SEO_META.en;
  const siteUrl = 'https://kaesthetic.world';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kaesthetic World',
    url: siteUrl,
    description: meta.description,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+34-662-408-398',
        contactType: 'sales',
        areaServed: 'EU',
        availableLanguage: ['Spanish', 'English', 'French', 'Portuguese', 'German', 'Italian', 'Greek', 'Russian'],
      },
      {
        '@type': 'ContactPoint',
        telephone: '+82-10-4453-8035',
        contactType: 'sales',
        areaServed: 'AS',
        availableLanguage: ['Korean', 'Japanese', 'Chinese'],
      },
    ],
    address: [
      {
        '@type': 'PostalAddress',
        addressLocality: 'Tarragona',
        addressCountry: 'ES',
      },
      {
        '@type': 'PostalAddress',
        addressLocality: 'Seoul',
        addressCountry: 'KR',
      },
    ],
    sameAs: [],
  };

  return (
    <Helmet>
      <html lang={language} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:site_name" content="Kaesthetic World" />
      <meta property="og:locale" content={meta.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />

      {/* hreflang tags for multi-language SEO */}
      {LANG_CODES.map((code) => (
        <link key={code} rel="alternate" hrefLang={code} href={`${siteUrl}?lang=${code}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />

      {/* Canonical */}
      <link rel="canonical" href={siteUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export { HelmetProvider };
export default SEOHead;
